import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { MdChevronLeft, MdChevronRight } from 'react-icons/md';
import { differenceInMinutes } from 'date-fns';
import {
  CalendarDate,
  getLocalTimeZone,
  now,
  parseAbsolute,
  toCalendarDate,
  toCalendarDateTime,
  ZonedDateTime,
} from '@internationalized/date';
import { DateInput, IconButton, Popover, PopoverContent, PopoverTrigger, Radio, RadioGroup, TimeInput } from '@ds';
import { AvailabilityFilterType } from '../types';
import { convertToIanaTimezone, getShortTzByIanaName } from '@/utils/helpers';

type AvailabilityPopoverProps = {
  title: string;
  type: AvailabilityFilterType;
  value: string;
  defaultValue: string;
  timezone: string;
  onChange: (value: string) => void;
};

export const AvailabilityPopover = ({
  title,
  type,
  defaultValue,
  value,
  timezone,
  onChange,
}: AvailabilityPopoverProps) => {
  const zonedDateTime = useRef(now(getLocalTimeZone()));
  const [availabilityFilterType, setAvailabilityFilterType] = useState<AvailabilityFilterType>(type);
  const [diffInMinutes, setDiffInMinutes] = useState<number>(0);

  const orTime = useMemo(() => parseAbsolute(defaultValue as string, timezone), [defaultValue, timezone]);
  const dateTime = useMemo(() => {
    return availabilityFilterType === AvailabilityFilterType.ORTime ? orTime : parseAbsolute(value as string, timezone);
  }, [availabilityFilterType, orTime, value, timezone]);

  useEffect(() => {
    setDiffInMinutes(differenceInMinutes(dateTime.toDate(), defaultValue as string));
  }, [dateTime, defaultValue]);

  const onTimeValueChange = (time: ZonedDateTime | null) => {
    if (!time) return;

    onChange(dateTime.set({ hour: time.hour, minute: time.minute }).toDate().toISOString());
    setAvailabilityFilterType(AvailabilityFilterType.Custom);
  };

  const onDateValueChange = (date: CalendarDate | null) => {
    if (!date) return;

    onChange(dateTime.set({ day: date.day, month: date.month, year: date.year }).toDate().toISOString());
    setAvailabilityFilterType(AvailabilityFilterType.Custom);
  };

  const getTimeDifference = (currentMinutes: number) => {
    if (currentMinutes !== 0) {
      let hours = Math.floor(currentMinutes / 60);
      const days = Math.floor(hours / 24);

      hours -= days * 24;
      const minutes = currentMinutes - days * 24 * 60 - hours * 60;

      return minutes > 0
        ? `
				OR ${dateTime.compare(toCalendarDateTime(parseAbsolute(value as string, timezone))) > 0 ? '+' : '-'}${`${days > 0 ? `${days}d` : ''} ${hours > 0 ? `${hours}h` : ''} ${minutes > 0 ? `${minutes}m` : ''}`}`
        : '';
    }

    return '';
  };

  const onDateFilterTypeChange = useCallback(
    (val: string) => {
      const currentValue = val as AvailabilityFilterType;
      setAvailabilityFilterType(currentValue);

      if (currentValue === AvailabilityFilterType.Now) {
        onChange(zonedDateTime.current.toDate().toISOString());
      }

      if (currentValue === AvailabilityFilterType.ORTime) {
        onChange(defaultValue as string);
      }
    },
    [onChange, defaultValue, zonedDateTime],
  );

  const dateValue = useMemo(() => toCalendarDate(dateTime), [dateTime]);

  const triggerText = useMemo(() => {
    if (availabilityFilterType === AvailabilityFilterType.ORTime) {
      return `${orTime.hour.toString().padStart(2, '0')}:${orTime.minute.toString().padStart(2, '0')} ${orTime.timeZone ? getShortTzByIanaName(convertToIanaTimezone(orTime.timeZone)) : ''} (OR + 4h)`;
    }

    return availabilityFilterType;
  }, [availabilityFilterType, orTime]);

  return (
    <Popover placement="bottom">
      <PopoverTrigger>
        <span className="text-base-5! text-s-sb mr-3 border-dotted border-b-1 border-black">{triggerText}</span>
      </PopoverTrigger>
      <PopoverContent className="w-[296px] justify-start items-start ">
        <div className="px-2 py-4">
          <span className="text-base-5 text-m-sb">{title}</span>
          <RadioGroup orientation="horizontal" value={availabilityFilterType} onValueChange={onDateFilterTypeChange}>
            <Radio className="w-[81px] max-w-none p-0 pt-3" key="Now" value={AvailabilityFilterType.Now} size="sm">
              Now
            </Radio>
            <Radio className="w-[81px] max-w-none p-0 pt-3" key="ORTime" value="OR time" size="sm">
              OR time
            </Radio>
            <Radio
              className="w-[81px] max-w-none p-0 pt-3"
              key="Custom"
              value={AvailabilityFilterType.Custom}
              size="sm"
            >
              Custom
            </Radio>
          </RadioGroup>
          <hr className="border-t-1 border-solid mt-3 mb-3 border-gray-300" />
          <div className="flex flex-row justify-between">
            <DateInput
              aria-label="availability-date-input"
              label={null}
              defaultValue={dateValue}
              onChange={onDateValueChange}
              size="lg"
              value={dateValue}
              classNames={{
                inputWrapper:
                  'hover:focus-within:bg-transparent hover:bg-transparent bg-transparent border-none shadow-none h-auto min-h-0 p-0',
                base: 'w-max self-center',
                segment: 'slashed-zero',
              }}
            />
            <div className="flex items-center">
              <IconButton
                variant="action"
                disableRipple
                disabled={
                  type === AvailabilityFilterType.Now && toCalendarDate(dateTime).compare(zonedDateTime.current) === 0
                }
                startContent={<MdChevronLeft size={24} />}
                onClick={() => onDateValueChange(toCalendarDate(dateTime.subtract({ days: 1 })))}
                className="items-start h-auto"
              />
              <IconButton
                variant="action"
                disableRipple
                startContent={<MdChevronRight size={24} />}
                onClick={() => onDateValueChange(toCalendarDate(dateTime.add({ days: 1 })))}
                className="items-start h-auto"
              />
            </div>
          </div>
          <hr className="border-t-1 border-solid mt-3 mb-3 border-gray-300" />
          <div className="flex flex-row justify-between">
            <TimeInput
              aria-label="availability-time-input"
              granularity="minute"
              label={null}
              defaultValue={dateTime}
              onChange={onTimeValueChange}
              value={dateTime}
              hourCycle={24}
              size="lg"
              classNames={{
                inputWrapper:
                  'hover:focus-within:bg-transparent hover:bg-transparent bg-transparent border-none shadow-none h-auto min-h-0 p-0',
                segment: 'mr-3 slashed-zero',
                base: 'w-max',
              }}
            />
            {availabilityFilterType === AvailabilityFilterType.Custom && (
              <div className="flex items-center">
                <span className="text-m-m text-base-20">{getTimeDifference(diffInMinutes)}</span>
              </div>
            )}
          </div>
        </div>
      </PopoverContent>
    </Popover>
  );
};
